import { useId } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { frameTransparentBackground } from "components/common/backgroundFrame";
import { FOCUS_RING } from "globalStyles";
import VisuallyHidden from "components/common/VisuallyHidden";
import {
    VOTEBOX_FONT_SIZE_BODY,
    VOTEBOX_MUTED_BACKGROUND_COLOR,
    VOTEBOX_NEUTRAL_OPTION_COLOR,
    VOTEBOX_OPTION_RADIUS,
    VOTEBOX_PANEL_PADDING,
    VOTEBOX_ROW_GAP,
    VOTEBOX_SUCCESS_COLOR,
    VOTEBOX_SWITCH_HEIGHT,
    VOTEBOX_SWITCH_KNOB_OFFSET,
    VOTEBOX_SWITCH_KNOB_SIZE,
    VOTEBOX_SWITCH_WIDTH,
    VOTEBOX_WHITE,
} from "../voteboxTokens";

type Props = {
    checked: boolean;
    onChange: (checked: boolean) => void;
    // In an election, switching it on also sends the vote of every proxy.
    isProxyVoteSentAutomatically?: boolean;
};

const ToggleSwitch = ({
    checked,
    onChange,
    isProxyVoteSentAutomatically = false,
}: Props) => {
    const { t } = useTranslation();
    const switchId = useId();
    const hintId = useId();

    return (
        <ToggleWrapper>
            <StyledLabel htmlFor={switchId}>
                {t("sameVoteForAllProxies")}
            </StyledLabel>
            <VisuallyHidden id={hintId}>
                {t(
                    isProxyVoteSentAutomatically
                        ? "sameVoteForAllProxiesSentHint"
                        : "sameVoteForAllProxiesHint",
                )}
            </VisuallyHidden>
            <CheckBoxWrapper>
                <CheckBox
                    id={switchId}
                    type="checkbox"
                    role="switch"
                    checked={checked}
                    onChange={() => onChange(!checked)}
                    aria-describedby={hintId}
                />
                <CheckBoxTrack aria-hidden="true" />
            </CheckBoxWrapper>
        </ToggleWrapper>
    );
};

const ToggleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    gap: ${VOTEBOX_ROW_GAP};
    padding: ${VOTEBOX_PANEL_PADDING};
    margin: 1rem -0.7rem 1rem -0.7rem;
    align-items: center;
    border-radius: ${VOTEBOX_OPTION_RADIUS};
    background-color: ${VOTEBOX_MUTED_BACKGROUND_COLOR};
    font-size: ${VOTEBOX_FONT_SIZE_BODY};
    ${frameTransparentBackground}
`;

const StyledLabel = styled.label`
    cursor: pointer;
`;

const CheckBoxWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
    width: ${VOTEBOX_SWITCH_WIDTH};
    height: ${VOTEBOX_SWITCH_HEIGHT};
`;

// Drawn under the transparent checkbox, which receives the clicks and the focus.
const CheckBoxTrack = styled.span`
    position: absolute;
    inset: 0;
    border-radius: calc(${VOTEBOX_SWITCH_HEIGHT} / 2);
    background: ${VOTEBOX_NEUTRAL_OPTION_COLOR};
    pointer-events: none;
    &::after {
        content: "";
        display: block;
        border-radius: 50%;
        width: ${VOTEBOX_SWITCH_KNOB_SIZE};
        height: ${VOTEBOX_SWITCH_KNOB_SIZE};
        margin: ${VOTEBOX_SWITCH_KNOB_OFFSET};
        background: ${VOTEBOX_WHITE};
        box-shadow: 1px 3px 3px 1px rgba(0, 0, 0, 0.2);
        transition: 0.2s;
    }
`;

const CheckBox = styled.input`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
    &:checked + ${CheckBoxTrack} {
        background: ${VOTEBOX_SUCCESS_COLOR};
        &::after {
            margin-left: calc(
                ${VOTEBOX_SWITCH_WIDTH} - ${VOTEBOX_SWITCH_KNOB_SIZE} -
                    ${VOTEBOX_SWITCH_KNOB_OFFSET}
            );
        }
    }
    &:focus-visible + ${CheckBoxTrack} {
        ${FOCUS_RING}
    }
`;

export default ToggleSwitch;
